import { ChangeEvent, startTransition, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  itemVariants,
  Label,
  RadioGroup,
  RadioGroupItem,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Alert, SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { getUploadFileErrorMessage } from '../../constants/errorsMapping.js';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import {
  useDataFileSheetNames,
  useParseFile,
  useSetDataFileSheetName,
} from '../../queries/index.js';
import { normalizeString } from '../../utils/transforms.js';
import { EmptyState, SkeletonRow } from '../atoms/index.js';

export const SheetSelectionDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId, dataFilePreview, showUploadFileDialog } =
    useDataFileContext();

  const { data: sheetNamesList = [], isLoading: isSheetNamesListLoading } =
    useDataFileSheetNames(dataFileId);
  const { mutateAsync: setSheetName, isPending: isSetSheetNamePending } =
    useSetDataFileSheetName(dataFileId);
  const { mutateAsync: parseFile, isPending: isParseFilePending } =
    useParseFile(dataFileId);

  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchValue(e.target.value);
    });
  };
  const handleSearchClear = () => {
    setSearchValue('');
  };

  const [selectedSheetName, setSelectedSheetName] = useState<null | string>(
    dataFilePreview.sheetName ?? null,
  );
  const [sheetsErrorsMap, setSheetsErrorsMap] = useState(() => {
    if (dataFilePreview.sheetName && dataFilePreview.parsingError) {
      return {
        [dataFilePreview.sheetName]: getUploadFileErrorMessage(
          t,
          dataFilePreview.parsingError,
        ),
      };
    }
    return {};
  });

  const handleReplaceFile = () => {
    onClose();
    showUploadFileDialog();
  };

  const handleSubmit = async () => {
    if (selectedSheetName) {
      try {
        await setSheetName({ sheetName: selectedSheetName });
        await parseFile({
          fileName: dataFilePreview.name as string,
          resetFile: false,
        });
      } catch (error) {
        setSheetsErrorsMap((prev) => ({
          ...prev,
          [selectedSheetName]: error.message,
        }));
      }
    }
  };

  const filteredSheetNamesList = sheetNamesList.reduce((acc, sheetName) => {
    const isMatchingSearchValue = normalizeString(sheetName).includes(
      normalizeString(searchValue),
    );

    if (isMatchingSearchValue) {
      acc.push(sheetName);
    }
    return acc;
  }, [] as string[]);

  const isTemplateSheetNameSelected =
    Boolean(dataFilePreview.sheetName) &&
    selectedSheetName === dataFilePreview.sheetName;

  const templateSheetIsNotFound =
    isTemplateSheetNameSelected &&
    !isSheetNamesListLoading &&
    !sheetNamesList.includes(dataFilePreview.sheetName as string);

  const isSelectedSheetValid =
    selectedSheetName &&
    sheetNamesList.includes(selectedSheetName) &&
    !sheetsErrorsMap[selectedSheetName];
  const isSubmitting = isSetSheetNamePending || isParseFilePending;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange} modal>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle>{t('Sheet selection')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('Choose a sheet from the uploaded file to continue.')}
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            <div className="flex w-full flex-col gap-4">
              {templateSheetIsNotFound ? (
                <Alert
                  severity="warning"
                  removable={false}
                  title={t(
                    `The sheet '{{sheetName}}' from the template is missing.`,
                    { sheetName: dataFilePreview.sheetName },
                  )}
                  description={t('Please choose another sheet.')}
                />
              ) : (
                <p className={typographyVariants({ variant: 'bodyLong01' })}>
                  {t('Select a sheet you want to work with to import the data')}
                </p>
              )}
              <SearchBar
                value={searchValue}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder={t('Search by name')}
                size="small"
                className="min-h-8 w-full"
              />
              {isSheetNamesListLoading ? (
                <div className="flex flex-col gap-2">
                  <SkeletonRow
                    rowsCount={5}
                    variants={[{ variant: 'rounded', className: 'flex-1 h-8' }]}
                  />
                </div>
              ) : (
                <RadioGroup
                  value={selectedSheetName ?? ''}
                  onValueChange={setSelectedSheetName}
                  className="flex max-h-[250px] flex-col gap-2 overflow-auto"
                >
                  {filteredSheetNamesList.map((sheetName) => {
                    const errorMessage = sheetsErrorsMap[sheetName];
                    const disabled = Boolean(errorMessage);
                    const selected = selectedSheetName === sheetName;

                    return (
                      <div key={sheetName}>
                        <Label
                          data-dd-action-name="Sheet name option"
                          aria-disabled={disabled}
                          variant="bodyShort01"
                          className={cn(
                            itemVariants({
                              selectionListItem: selected
                                ? 'selected'
                                : 'default',
                            }),
                            'm-0 cursor-pointer shadow-sm aria-disabled:pointer-events-none aria-disabled:cursor-auto',
                            errorMessage && 'border-error-main',
                          )}
                        >
                          <RadioGroupItem
                            value={sheetName}
                            disabled={disabled}
                          />
                          <span>{sheetName}</span>
                        </Label>
                        {errorMessage ? (
                          <span
                            className={typographyVariants({
                              variant: 'caption1',
                              className: 'text-error',
                            })}
                          >
                            {errorMessage}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </RadioGroup>
              )}
              {searchValue && !filteredSheetNamesList.length && (
                <EmptyState
                  text={t('No results found for "{{searchValue}}"', {
                    searchValue,
                  })}
                  description={t(
                    'Try adjusting your search to find what you are looking for.',
                  )}
                  className="border-none shadow-none"
                />
              )}
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              onClick={handleReplaceFile}
              variant="text"
              size="large"
              data-dd-action-name="Replace file"
            >
              {t('Replace file')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isSelectedSheetValid || isSubmitting}
              size="large"
              data-dd-action-name="Confirm"
            >
              {t('Confirm')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
